import { useEffect, useState } from 'react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { fetchMaps, type MapInfo } from '@/lib/maps'
import { FACTIONS, teamsTitle, type MatchTeam } from '@/lib/factions'
import FactionIcon from '@/components/matches/FactionIcon'
import { useLanguage } from '@/contexts/LanguageContext'

/** Radix Select rejects "" as a value; these stand in for "not set". */
const NO_MAP = '__none__'
const NO_FACTION = '__none__'

/** Wardogs matches are three-sided; the form always offers exactly three rows. */
const TEAM_SLOTS = 3
const EMPTY_TEAMS: MatchTeam[] = Array.from({ length: TEAM_SLOTS }, () => ({ name: '', faction: '' }))

/** Existing teams padded back out to three editable rows. */
function teamSlots(teams?: MatchTeam[]): MatchTeam[] {
  return EMPTY_TEAMS.map((blank, i) => ({
    name: teams?.[i]?.name ?? blank.name,
    faction: teams?.[i]?.faction ?? blank.faction,
  }))
}

export interface MatchDraft {
  event_id?: string
  title: string
  start_time?: number | null
  briefing_time?: number | null
  map_name?: string
  description?: string
  teams?: MatchTeam[]
}

// <input type="date"> and type="time" want local wall-clock strings; the API
// speaks unix seconds. These two convert in both directions.
function splitTimestamp(ts?: number | null): { date: string; time: string } {
  if (!ts) return { date: '', time: '' }
  const d = new Date(ts * 1000)
  const p = (n: number) => String(n).padStart(2, '0')
  return {
    date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
    time: `${p(d.getHours())}:${p(d.getMinutes())}`,
  }
}

function joinTimestamp(date: string, time: string): number | null {
  if (!date || !time) return null
  const ms = new Date(`${date}T${time}`).getTime()
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000)
}

/** The briefing shares the match's date field. A briefing clock later than the
 *  kick-off (23:30 for a 00:15 match) can only mean the evening before. */
function briefingTimestamp(date: string, time: string, start: number | null): number | null {
  const ts = joinTimestamp(date, time)
  if (ts === null || start === null) return null
  return ts >= start ? ts - 86400 : ts
}

export default function MatchFormDialog({ open, match, onClose, onSaved }: {
  open: boolean
  /** Existing match to edit; omit to create a new one. */
  match?: MatchDraft | null
  onClose: () => void
  onSaved: (eventId: string) => void
}) {
  const { T } = useLanguage()
  const [title, setTitle] = useState('')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [briefing, setBriefing] = useState('')
  const [mapName, setMapName] = useState('')
  const [teams, setTeams] = useState<MatchTeam[]>(EMPTY_TEAMS)
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Same catalog the strategy editor draws on — one source of map names.
  const [maps, setMaps] = useState<MapInfo[]>([])

  useEffect(() => { fetchMaps().then(setMaps).catch(() => setMaps([])) }, [])

  // Reset the form each time the dialog opens, from the match being edited.
  useEffect(() => {
    if (!open) return
    const { date: d, time: t } = splitTimestamp(match?.start_time)
    setTitle(match?.title ?? '')
    setDate(d)
    setTime(t)
    setBriefing(splitTimestamp(match?.briefing_time).time)
    setMapName(match?.map_name ?? '')
    setTeams(teamSlots(match?.teams))
    setDescription(match?.description ?? '')
    setError(null)
  }, [open, match])

  const startTime = joinTimestamp(date, time)
  const briefingTime = briefingTimestamp(date, briefing, startTime)
  // The team names spell the title out ("TLL vs WDUK vs 331"), so a match named
  // by its sides needs no title typed — the field shows what it will be called.
  const derivedTitle = teamsTitle(teams)
  const canSave = !!(title.trim() || derivedTitle) && startTime !== null

  const setTeam = (i: number, patch: Partial<MatchTeam>) =>
    setTeams(prev => prev.map((t, j) => (j === i ? { ...t, ...patch } : t)))

  const handleSave = async () => {
    if (!canSave) { setError(T.matches.formRequired); return }
    setSaving(true)
    setError(null)
    try {
      const editing = !!match?.event_id
      const res = await fetch(
        editing ? `/api/matches/${match!.event_id}` : '/api/matches',
        {
          method: editing ? 'PUT' : 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: title.trim() || derivedTitle,
            start_time: startTime,
            briefing_time: briefingTime,
            map_name: mapName,
            teams: teams
              .filter(t => t.name.trim())
              .map(t => ({ name: t.name.trim(), faction: t.faction ?? '' })),
            description: description.trim(),
          }),
        },
      )
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        setError(d.detail ?? T.matches.saveError)
        return
      }
      const d = await res.json().catch(() => ({}))
      onSaved(d.event_id ?? match?.event_id ?? '')
      onClose()
    } catch {
      setError(T.matches.serverError)
    } finally {
      setSaving(false)
    }
  }

  const fieldLabel = 'text-[10px] font-semibold tracking-widest text-muted-foreground/50 uppercase'

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{match?.event_id ? T.matches.editMatch : T.matches.newMatch}</DialogTitle>
          <DialogDescription>{T.matches.postedToForum}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className={fieldLabel}>{T.matches.formTitle}</label>
            <Input
              value={title}
              autoFocus
              onChange={e => setTitle(e.target.value)}
              placeholder={derivedTitle || T.matches.formTitlePh}
            />
          </div>

          <div className="space-y-1.5">
            <label className={fieldLabel}>{T.matches.formTeams}</label>
            <div className="space-y-2">
              {teams.map((t, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={t.name}
                    onChange={e => setTeam(i, { name: e.target.value })}
                    placeholder={T.matches.formTeamPh.replace('{n}', String(i + 1))}
                    className="flex-1"
                  />
                  {/* The faction is genuinely optional — it is often unknown
                      when the match is booked. */}
                  <Select
                    value={t.faction || NO_FACTION}
                    onValueChange={v => setTeam(i, { faction: v === NO_FACTION ? '' : v })}
                  >
                    <SelectTrigger className="w-40 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_FACTION}>
                        <span className="text-muted-foreground">{T.matches.formFactionNone}</span>
                      </SelectItem>
                      {FACTIONS.map(f => (
                        <SelectItem key={f.slug} value={f.slug}>
                          <span className="flex items-center gap-2">
                            <FactionIcon faction={f.slug} className="h-3.5 w-3.5" />
                            {f.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          {/* Dar ekranda tarih tam satır, briefing + maç saati altında yan yana;
              sm+ üçü tek satırda, saatler sabit genişlikte. */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex-1 space-y-1.5">
              <label className={fieldLabel}>{T.matches.formDate}</label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="flex gap-3">
              {/* Briefing first: it is the earlier of the two and the one players
                  have to show up for. Optional — not every match has one. */}
              <div className="flex-1 space-y-1.5 sm:w-28 sm:flex-none">
                <label className={fieldLabel}>{T.matches.formBriefing}</label>
                <Input type="time" value={briefing} onChange={e => setBriefing(e.target.value)} />
              </div>
              <div className="flex-1 space-y-1.5 sm:w-28 sm:flex-none">
                <label className={fieldLabel}>{T.matches.formTime}</label>
                <Input type="time" value={time} onChange={e => setTime(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className={fieldLabel}>{T.matches.formMap}</label>
            {/* Radix has no empty-string value, so "not set" gets a sentinel. */}
            <Select
              value={mapName || NO_MAP}
              onValueChange={v => setMapName(v === NO_MAP ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_MAP}>
                  <span className="text-muted-foreground">{T.matches.formMapNone}</span>
                </SelectItem>
                {maps.map(m => <SelectItem key={m.name} value={m.label}>{m.label}</SelectItem>)}
                {/* A map dropped from the catalog must not vanish from an existing match. */}
                {mapName && !maps.some(m => m.label === mapName) && (
                  <SelectItem value={mapName}>{mapName}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className={fieldLabel}>{T.matches.formDescription}</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={T.matches.formDescriptionPh}
              rows={3}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none resize-y placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/20"
            />
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {T.matches.cancel}
          </Button>
          <Button onClick={handleSave} disabled={saving || !canSave}>
            {saving ? T.matches.saving : match?.event_id ? T.matches.save : T.matches.createMatch}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
